<script lang="ts" setup>
import { ref } from 'vue'
import type { $Fetch } from 'ofetch'
import RoomieButtonComponent from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import RoomieLoader from '@lahaus-roomie/roomie/src/components/RoomieLoader/index.vue'
import RoomieOtpInput from '@lahaus-roomie/roomie/src/components/RoomieOtpInput/index.vue'
import { STATUS as INPUT_STATUS } from '@lahaus-roomie/roomie/src/components/RoomieInput/index.vue'
import AuthService from '@/services/authService'
import { getCaptchaToken } from '@/components/App/LoginForm/utils'
import { parseJwt } from '@/stores/userStore/jwt.mapper'

interface Props {
  phone: string
  phonePrefix: string
  phoneNumber: string
  name: string
  email: string
  siteKey: string
  dataAttributes?: Record<string, any>
}

const props = defineProps<Props>()

const emit = defineEmits(['submit', 'back', 'error'])

const status = ref<'otp' | 'loader'>('otp')
const otpValue = ref('')
const otpButtonEnabled = ref(false)
const otpInputStaus = ref(INPUT_STATUS.default)
const otpInputStausMsg = ref('')

const otpResendTimer = ref(0)
const otpEnableResend = ref(true)

const { $config } = useNuxtApp()

const authService = new AuthService({
  apiFetch: globalThis.$fetch as $Fetch,
  authUrl: $config.public.authorizationApiUrl as string,
  authId: $config.public.laHausAuthId as string,
  xApplicationId: $config.public.environment
})

const { t } = useI18n()
const { $useLhId } = useNuxtApp()

const timeUp = () => {
  otpEnableResend.value = true
}

const filledOtp = () => {
  otpButtonEnabled.value = true
  otpInputStaus.value = INPUT_STATUS.default
}

const handleOtpSubmit = async () => {
  try {
    status.value = 'loader'
    const captchaToken = await getCaptchaToken(props.siteKey)
    const result = await authService.challenge(otpValue.value, captchaToken)

    if (result?.auth_result) {
      const accessToken = result?.auth_result?.access_token
      const parsedToken = parseJwt(accessToken)
      const userId = parsedToken?.cdp_id

      const userData = {
        name: props.name,
        email: props.email,
        phone: props.phone,
        id: userId
      }

      emit('submit', userData)
    } else {
      status.value = 'otp'
      otpInputStaus.value = INPUT_STATUS.error
      emit('error', 'Registration Form | Verificación de OTP no tiene tokens de acceso', result)
    }
  } catch (error: any) {
    otpInputStaus.value = INPUT_STATUS.error
    if (['invalid_code'].includes(error?.code)) {
      status.value = 'otp'
      otpInputStausMsg.value = t(`errors.${error?.code}`)
    } else {
      status.value = 'otp'
      emit('error', 'Registration Form | Verificación de OTP fallo', {
        error,
        message: error.message
      })
    }
  }
}

const sendOtpCode = async (contactMedia = 'SMS') => {
  const isPhoneValid = !!props.phoneNumber

  if (!isPhoneValid) {
    emit('back')
    return false
  }

  status.value = 'loader'

  try {
    await authService.authorizerToken()

    const captchaToken = await getCaptchaToken(props.siteKey)

    const result = await authService.authorizer(props.phonePrefix, props.phoneNumber, captchaToken, contactMedia)

    if (result?.challenge && result?.challenge === 'confirm_otp') {
      status.value = 'otp'
      return true
    } else {
      status.value = 'otp'
      emit('error', 'Registration Form | El resultado del authorizer no fue confirm_otp', result)
      return false
    }
  } catch (error: any) {
    status.value = 'otp'
    emit('error', 'Registration Form | Error enviando el OTP', {
      error,
      message: error?.message
    })
    return false
  }
}

const resendOtpWhatsApp = async () => {
  otpResendTimer.value = 60
  otpEnableResend.value = false

  await sendOtpCode('WHATSAPP')
}

const resendOtpSms = async () => {
  otpResendTimer.value = 60
  otpEnableResend.value = false

  await sendOtpCode('SMS')
}

defineExpose({
  sendOtpCode
})
</script>

<i18n src="./i18n.json" lang="json"></i18n>

<template>
  <div
    v-if="status === 'otp'"
    class="flex flex-col gap-42">
    <div>
      <h2
        v-sanitize.basic="t('otpTitle')"
        class="text-18 font-medium mt-8 text-carbon-800" />

      <p
        v-sanitize.basic="t('otpDescription', { lastDigits: phone.slice(-2) })"
        class="text-14 text-carbon-300" />
    </div>

    <RoomieOtpInput
      id="roomie-input-otp"
      v-model="otpValue"
      :status="otpInputStaus"
      :helper-text="otpInputStausMsg"
      :tracking-lh-id="$useLhId({ ...dataAttributes?.dataLh, A: 'escribir', C: 'otp' })"
      :time-limit="otpResendTimer"
      @time-up="timeUp"
      @filled="filledOtp" />

    <div
      v-if="otpEnableResend === true"
      class="flex flex-col gap-8">
      <p
        class="text-14 text-carbon-300">
        {{ t('otpResend') }}
      </p>

      <a
        v-lh-id="{ ...dataAttributes?.dataLh, A: 'reenviar', C: t('otpResendCtaSms') }"
        class="text-pine-600 underline cursor-pointer text-14"
        @click="resendOtpSms">
        {{ t('otpResendCtaSms') }}
      </a>

      <a
        v-lh-id="{ ...dataAttributes?.dataLh, A: 'reenviar', C: t('otpResendCtaWhastapp') }"
        class="text-pine-600 underline cursor-pointer text-14"
        @click="resendOtpWhatsApp">
        {{ t('otpResendCtaWhastapp') }}
      </a>
    </div>

    <div v-else>
      <p class="text-14 text-pine-600">
        {{ t('otpResend') }}
      </p>

      <p class="text-14">
        {{ t('otpResendWaiting') }}
      </p>
    </div>

    <div>
      <RoomieButtonComponent
        id="otp-button"
        class="w-full font-medium"
        :data-lh-id="$useLhId({ ...dataAttributes?.dataLh, A: 'validar', C: t('otpCta') })"
        :aria-label="t('otpCta')"
        :disabled="!otpButtonEnabled"
        variant="primary"
        @click="handleOtpSubmit">
        <span class="flex">
          {{ t('otpCta') }}
        </span>
      </RoomieButtonComponent>

      <a
        id="login-form-go-back-cta"
        v-lh-id="{ ...dataAttributes?.dataLh, A: 'regresar', C: t('otpGoBackCta') }"
        class="block text-pine-600 underline cursor-pointer text-center mt-16"
        @click="emit('back')">
        {{ t('otpGoBackCta') }}
      </a>
    </div>
  </div>

  <div
    v-else
    class="h-[337px] flex items-center justify-center">
    <RoomieLoader
      size="xl"
      color="primary" />
  </div>
</template>
